import * as d3 from 'd3';
import $ from 'jquery';
import _ from 'underscore';

import * as sequtil from 'molbio/SeqUtil';
import {ellipsize} from 'util/NameUtil';
import {getSVGHeight, appendSVGToSVG} from 'util/SVGUtil';
import * as TaskRunner from 'util/TaskRunner';

interface PageInfoOptions {
  landscape: boolean;
  width: number;
  pageWidth: string;
  height: number;
  pageHeight: string;
  headerTop: number;
  headerLeft: number;
  headerRight: number;
  footerBottom: number;
  footerLeft: number;
  footerRight: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  css: null | string;
}

class PageInfo {
  protected readonly options: PageInfoOptions;
  readonly landscape: boolean;
  readonly width: number;
  readonly pageWidth: string;
  readonly height: number;
  readonly pageHeight: string;
  readonly headerTop: number;
  readonly headerLeft: number;
  readonly headerRight: number;
  readonly footerBottom: number;
  readonly footerLeft: number;
  readonly footerRight: number;
  readonly marginTop: number;
  readonly marginBottom: number;
  readonly marginLeft: number;
  readonly marginRight: number;
  readonly css: null | string;

  constructor(options: Partial<PageInfoOptions>) {
    this.options = _.defaults(options, {
      landscape: false,
      width: 850,
      pageWidth: '8.5in',
      height: 1100,
      pageHeight: '11in',
      headerTop: 50,
      headerLeft: 25,
      headerRight: 25,
      footerBottom: 25,
      footerLeft: 25,
      footerRight: 25,
      marginTop: 80,
      marginBottom: 50,
      marginLeft: 50,
      marginRight: 50,
      css: null,
    });

    this.marginTop = this.options.marginTop;
    this.marginBottom = this.options.marginBottom;
    this.marginLeft = this.options.marginLeft;
    this.marginRight = this.options.marginRight;
    this.css = this.options.css;
    this.headerTop = this.options.headerTop;
    this.headerLeft = this.options.headerLeft;
    this.headerRight = this.options.headerRight;
    this.footerBottom = this.options.footerBottom;
    this.footerLeft = this.options.footerLeft;
    this.footerRight = this.options.footerRight;
    this.landscape = this.options.landscape;

    // Swap width<->height and pageWidth<->pageHeight when in landscape mode.
    this.width = this.landscape ? this.options.height : this.options.width;
    this.height = this.landscape ? this.options.width : this.options.height;
    this.pageWidth = this.landscape ? this.options.pageHeight : this.options.pageWidth;
    this.pageHeight = this.landscape ? this.options.pageWidth : this.options.pageHeight;
  }

  innerWidth(): number {
    return this.width - this.marginLeft - this.marginRight;
  }

  innerHeight(): number {
    return this.height - this.marginTop - this.marginBottom;
  }
}

class Page extends PageInfo {
  private spacer: number;
  lowestY: number;
  bpStart: null | number;
  bpEnd: null | number;
  svg: SVGSVGElement;

  private header: d3.Selection<SVGGElement, unknown, null, undefined>;
  private headerRightText: d3.Selection<SVGTextElement, unknown, null, undefined>;
  private headerCentreText: d3.Selection<SVGTextElement, unknown, null, undefined>;
  private footer: d3.Selection<SVGGElement, unknown, null, undefined>;
  private footerLeftText: d3.Selection<SVGTextElement, unknown, null, undefined>;
  private footerRightText: d3.Selection<SVGTextElement, unknown, null, undefined>;

  constructor(options: Partial<PageInfoOptions>) {
    super(options);
    this.lowestY = this.marginTop;
    this.spacer = 20;
    this.bpStart = null;
    this.bpEnd = null;
    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    d3.select(this.svg)
      .attr('xmlns', 'http://www.w3.org/2000/svg')
      .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink')
      .attr('width', this.pageWidth)
      .attr('height', this.pageHeight)
      .attr('viewBox', `0 0 ${this.width} ${this.height}`);

    this.header = d3
      .select(this.svg)
      .append<SVGGElement>('svg:g')
      .classed('header', true)
      .attr('transform', `translate(0,${this.headerTop})`);

    this.headerRightText = this.header
      .append<SVGTextElement>('svg:text')
      .classed('right', true)
      .attr('x', this.width - this.headerRight)
      .attr('text-anchor', 'end');

    this.headerCentreText = this.header
      .append<SVGTextElement>('svg:text')
      .classed('centre', true)
      .attr('x', this.width / 2)
      .attr('text-anchor', 'middle');

    this.footer = d3
      .select(this.svg)
      .append<SVGGElement>('svg:g')
      .classed('footer', true)
      .attr('transform', `translate(0,${this.height - this.footerBottom})`);

    this.footerLeftText = this.footer
      .append<SVGTextElement>('svg:text')
      .classed('left', true)
      .attr('x', this.footerLeft)
      .attr('text-anchor', 'start');

    this.footerRightText = this.footer
      .append<SVGTextElement>('svg:text')
      .classed('right', true)
      .attr('x', this.width - this.footerRight)
      .attr('text-anchor', 'end');

    if (this.css != null) {
      // Inkscape/cairo doesn't like multiple fonts specified.
      const css = this.css.replace(/font-family:.*?;/, 'font-family: "Inconsolata";');
      const defs = d3.select(this.svg).append('svg:defs');
      defs.append('svg:style').text(css);
    }
  }

  countSlice(slice: {start: number; end: number}): void {
    if (this.bpStart == null || slice.start < this.bpStart) {
      this.bpStart = slice.start;
    }
    if (this.bpEnd == null || slice.end > this.bpEnd) {
      this.bpEnd = slice.end;
    }
  }

  setHeader(centre: string, right: string): void {
    centre = ellipsize(centre, this.landscape ? 115 : 70);
    this.headerCentreText.text(centre);
    this.headerRightText.text(right);
  }

  setFooter(left: string, right: string): void {
    left = ellipsize(left, this.landscape ? 160 : 120);
    this.footerLeftText.text(left);
    this.footerRightText.text(right);
  }

  hasSpace(svgs: Array<SVGSVGElement>): boolean {
    let height = 0;
    svgs.forEach((svg) => {
      height += getSVGHeight(svg) + this.spacer;
    });
    height -= this.spacer;
    return this.lowestY + height < this.height - this.marginBottom;
  }

  addG(g: SVGGElement, height: number): void {
    d3.select(g).attr('transform', `translate(${this.marginLeft}, ${this.lowestY})`);
    $(this.svg).append(g);
    this.lowestY += height + this.spacer;
  }

  addSpacer(): void {
    this.lowestY += this.spacer;
  }

  addSVG(svg: SVGSVGElement): void {
    appendSVGToSVG({
      parentSVG: this.svg,
      childSVG: svg,
      transform: `translate(${this.marginLeft}, ${this.lowestY})`,
    });
    this.lowestY += getSVGHeight(svg) + this.spacer;
  }
}

/**
 * Converts an SVG node into a string, stripping any of the non-printable ASCII characters.
 * @param  {SVGSVGElement} svg
 * @return {String}
 */
function _svgToStr(svg: SVGSVGElement): string {
  // eslint-disable-next-line no-control-regex
  return new XMLSerializer().serializeToString(svg).replace(/[\u0000-\u001f]+/g, '');
}

class Pagination extends PageInfo {
  static PAGE_LIMIT = 200;
  page!: Page;
  pages: Array<Page>;
  fits: boolean;

  constructor(options: Partial<PageInfoOptions>) {
    super(options);
    this.pages = [];
    this.fits = true;
    this.newPage();
  }

  truncated(): boolean {
    return !this.fits;
  }

  newPage(): void {
    this.page = new Page(this.options);
    this.pages.push(this.page);
  }

  addTitle(title: string): void {
    title = ellipsize(title, this.page.landscape ? 65 : 50);
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    d3.select(g)
      .append('text')
      .attr('x', this.innerWidth() / 2)
      .attr('text-anchor', 'middle')
      .attr('font-size', 24)
      .text(title);
    this.page.addG(g, 0);
  }

  addLineBreak(): void {
    this.page.lowestY = this.page.height;
  }

  addSVGGroup(svgs: Array<SVGSVGElement>, slice: {start: number; end: number} | null = null): void {
    // Ideally the SVGs are kept together.
    if (this.page.hasSpace(svgs)) {
      // If a page has space for all SVGs, add them.
      svgs.forEach((svg) => {
        this.page.addSVG(svg);
      });
      if (slice != null) {
        this.page.countSlice(slice);
      }
      this.page.addSpacer();
    } else if (new Page(this.options).hasSpace(svgs) && this.pages.length < Pagination.PAGE_LIMIT) {
      // Otherwise, see if they will all fit on the same page.
      this.newPage();
      svgs.forEach((svg) => {
        this.page.addSVG(svg);
      });
      if (slice != null) {
        this.page.countSlice(slice);
      }
      this.page.addSpacer();
    } else {
      // If not, allow them to break up, but ensure the next group
      // starts on its own page.
      svgs.forEach((svg) => {
        this.addSVG(svg, slice);
      });
      this.addLineBreak();
    }
  }

  addSVG(svg: SVGSVGElement, slice: {start: number; end: number} | null = null): boolean {
    let hasSpace = true;
    if (!this.page.hasSpace([svg])) {
      if (this.pages.length >= Pagination.PAGE_LIMIT) {
        hasSpace = false;
        this.fits = false;
      } else {
        this.newPage();
      }
    }

    if (hasSpace) {
      this.page.addSVG(svg);
      if (slice != null) {
        this.page.countSlice(slice);
      }
    }
    return hasSpace;
  }

  setHeaders(title: string, seqLength: number, startIndex: number): void {
    const now = new Date();
    const nowStr = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
    this.pages.forEach((page, i) => {
      let titleHeader;
      if (this.pages.length === 1) {
        titleHeader = '';
      } else {
        const [start, end] = sequtil.modelRangeToDisplay(page.bpStart, page.bpEnd, seqLength, startIndex);
        titleHeader = i > 0 ? `${title} ` : '';
        titleHeader += `(from ${start}-${end} bp)`;
      }
      page.setHeader(titleHeader, nowStr);
      page.setFooter(window.location.href, `${i + 1}/${this.pages.length}`);
    });
  }

  svgs(): Array<SVGSVGElement> {
    return this.pages.map((page) => page.svg);
  }

  toPDF(filename: string): JQueryPromise<string> {
    return TaskRunner.runTask({
      url: '/1/api/svgs-to-pdf',
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        filename,
        svgs: this.pages.map((page) => _svgToStr(page.svg)),
      }),
    });
  }
}

export default Pagination;
